import type { SalesDisplayLoopSlide } from "@/lib/sales-display-loop";
import type { SalesDisplaySlide } from "@/lib/sales-display-slides";

/**
 * VIB-2705: kontrakten mellom slideoppsettet i Innstillinger og den ekte
 * salgsdisplay-siden som vises i en iframe ved siden av det.
 *
 * Hvorfor iframe og ikke en egen gjengivelse: previewen skal vise loopen slik
 * TV-en faktisk kjører den, inkludert widget-skjermene med ekte tall. Gjengir vi
 * dem på nytt i innstillinger, får vi en andre gjengivelsessti som kan komme i
 * utakt uten at noen merker det — nøyaktig fella `DisplayContentSlide` var.
 *
 * Indeksen og totalen eies av siden i iframen, ikke av foreldren. Foreldren ber
 * om «neste», «forrige» eller «hopp hit», og får tilbake hvor loopen står.
 * Alternativet, at foreldren regner ut loopen selv, ville krevd hele
 * salgsdashboard-datalasten i en innstillingsside OG en andre kopi av
 * ekspansjonsreglene i `configuredSalesDisplayLoop`.
 */

/**
 * Adressen salgsskjermene står på.
 *
 * Egen rute utenfor AppLayout, altså uten sidemeny og toppbar: en TV skal vise
 * tallene, ikke appens navigasjon. `/display/salg` finnes fortsatt som
 * inne-i-appen-visning for den som bare vil kikke.
 */
export const SALES_SCREEN_PATH = "/skjerm/salg";

/** Query-parameteren som setter salgsdisplayet i preview-modus. */
export const SALES_DISPLAY_PREVIEW_PARAM = "preview";
export const SALES_DISPLAY_PREVIEW_VALUE = "oppsett";

/**
 * Begge previewene kjører i iframe, og bredden er den samme som en TV-flate.
 *
 * Grunnen til iframe også for utkastet, som ikke er lagret og altså ikke finnes
 * i loopen: `ConfiguredSalesDisplaySlide` setter skriftstørrelsen med
 * `clamp(52px, 7vw, 112px)`. `vw` måles mot vindu-bredden, ikke mot boksen
 * komponenten står i. Gjengir vi den rett i en liten preview-rute på
 * innstillingssiden, regnes 7vw av HELE nettleservinduet, og tittelen renner
 * over. I en iframe er `vw` iframens egen bredde, og da stemmer den med TV-en.
 */
export const SALES_DISPLAY_PREVIEW_WIDTH = 1280;
export const SALES_DISPLAY_PREVIEW_HEIGHT = 720;

/** Utkastet under redigering, sendt inn til utkast-ruta. Ikke lagret noe sted. */
export interface SalesDisplayDraftMessage {
  type: "sales-display-preview/draft";
  slide: SalesDisplaySlide | null;
}

export function isSalesDisplayDraftMessage(
  value: unknown,
): value is SalesDisplayDraftMessage {
  if (typeof value !== "object" || value === null) return false;
  const message = value as Partial<SalesDisplayDraftMessage>;
  return message.type === "sales-display-preview/draft" && "slide" in message;
}

/**
 * Sendes fra utkast-flata når den faktisk lytter.
 *
 * `onLoad` på iframen er for tidlig: da er dokumentet lastet, men React har
 * ikke montert og lytteren finnes ikke ennå. Et utkast sendt i det vinduet er
 * borte, og previewen blir stående tom til neste tastetrykk — eller for alltid,
 * om brukeren er ferdig med å skrive.
 */
export const SALES_DISPLAY_DRAFT_READY = "sales-display-preview/draft-ready";

export function isSalesDisplayDraftReady(value: unknown): boolean {
  return (
    typeof value === "object" &&
    value !== null &&
    (value as { type?: unknown }).type === SALES_DISPLAY_DRAFT_READY
  );
}

/** Fra foreldren til iframen. */
export type SalesDisplayPreviewCommand =
  | { type: "sales-display-preview/command"; action: "next" }
  | { type: "sales-display-preview/command"; action: "prev" }
  | { type: "sales-display-preview/command"; action: "goto"; index: number };

/** Én skjerm i loopen, slik foreldren trenger å kjenne den. */
export interface SalesDisplayPreviewScreen {
  /** Loop-slidens id. Ikke stabil på tvers av lagringer, kun til React-keys. */
  id: string;
  label: string;
  durationSeconds: number;
}

/** Fra iframen til foreldren, ved hver endring i loopen eller posisjonen. */
export interface SalesDisplayPreviewState {
  type: "sales-display-preview/state";
  index: number;
  screens: SalesDisplayPreviewScreen[];
}

export function isSalesDisplayPreviewCommand(
  value: unknown,
): value is SalesDisplayPreviewCommand {
  if (typeof value !== "object" || value === null) return false;
  const message = value as Partial<SalesDisplayPreviewCommand>;
  if (message.type !== "sales-display-preview/command") return false;
  return (
    message.action === "next" ||
    message.action === "prev" ||
    (message.action === "goto" && typeof (message as { index?: unknown }).index === "number")
  );
}

export function isSalesDisplayPreviewState(
  value: unknown,
): value is SalesDisplayPreviewState {
  if (typeof value !== "object" || value === null) return false;
  const message = value as Partial<SalesDisplayPreviewState>;
  return (
    message.type === "sales-display-preview/state" &&
    typeof message.index === "number" &&
    Array.isArray(message.screens)
  );
}

/**
 * Menneskelig navn på én skjerm i loopen.
 *
 * Widget-radene i oppsettet er IKKE én skjerm hver: «Hall of Fame» blir tre og
 * «Salgspuls + alle selgere» blir én per salgsenhet (se
 * `configuredSalesDisplayLoop`). Derfor navngis hver ekspanderte skjerm for seg
 * — det er hele grunnen til at previewen viser loopen og ikke bare lista.
 */
export function salesDisplayScreenLabel(
  slide: SalesDisplayLoopSlide,
  unitNameById: Map<string, string>,
): string {
  switch (slide.kind) {
    case "team-goal":
      return "Måltall";
    case "sales-pulse":
      return "Salgspuls";
    case "sales-unit":
      return unitNameById.get(slide.unitId) ?? "Salgsenhet";
    case "hall-of-fame-timepris":
      return "Hall of Fame · beste timepris";
    case "hall-of-fame-geek":
      return "Hall of Fame · beste GEEK under budsjett";
    case "hall-of-fame-sales-unit-rate":
      return "Hall of Fame · reell timepris per selger";
    case "message":
      return slide.slide.title || "Beskjed";
    case "image":
      return slide.slide.title || "Bilde";
    case "video":
      return slide.slide.title || "Video";
  }
}

export function salesDisplayPreviewScreens(
  slides: SalesDisplayLoopSlide[],
  unitNameById: Map<string, string>,
): SalesDisplayPreviewScreen[] {
  return slides.map((slide) => ({
    id: slide.id,
    label: salesDisplayScreenLabel(slide, unitNameById),
    durationSeconds: slide.durationSeconds,
  }));
}

/** Hvor lang hele loopen er. Summen, ikke raden — widgets teller alle sine skjermer. */
export function salesDisplayLoopSeconds(screens: SalesDisplayPreviewScreen[]): number {
  return screens.reduce((total, screen) => total + screen.durationSeconds, 0);
}

/** «2 min 15 sek», eller «45 sek» når loopen er under minuttet. */
export function formatLoopDuration(totalSeconds: number): string {
  if (totalSeconds < 60) return `${totalSeconds} sek`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} sek`;
}
